import '@xyflow/react/dist/style.css'
import { ConfirmDialog } from '@/components/primitives/ConfirmDialog'
import { ErrorState } from '@/components/primitives/ErrorState'
import { btnPrimary, btnSecondary, iconBtn } from '@/components/record-sheet/styles'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { API_BASE_URL, ApiError } from '@/lib/api'
import { notifyError, queryClient } from '@/lib/query'
import { cn } from '@/lib/utils'
import {
  type Graph,
  type GraphEdge,
  type GraphNode,
  NODE_TYPES,
  type NodeData,
  type NodeType,
  RUN_ACTIVE,
  type StepStatus,
  type WorkflowDetail,
  type WorkflowRunStep,
  buildWorkflow,
  cancelRun,
  deleteWorkflow,
  fetchCatalog,
  fetchRun,
  fetchWorkflow,
  runWorkflow,
  saveWorkflow,
  stopBuild
} from '@/lib/workflows'
import { useQuery } from '@tanstack/react-query'
import {
  Background,
  BackgroundVariant,
  type Connection,
  Controls,
  type EdgeChange,
  MarkerType,
  type NodeChange,
  ReactFlow,
  ReactFlowProvider,
  addEdge,
  useEdgesState,
  useNodesState,
  useReactFlow
} from '@xyflow/react'
import {
  ArrowLeft,
  Check,
  Loader2,
  Maximize2,
  MoreHorizontal,
  Play,
  Plus,
  Save,
  Sparkles,
  Trash2,
  X
} from 'lucide-react'
import { useTheme } from 'next-themes'
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { ChiefOrb } from '../AgentAvatar'
import { ConnectionLine, type EdgeState, type FlowEdge, edgeColor, edgeTypes } from './FlowEdge'
import { NodeInspector } from './NodeInspector'
import { BuildPanel, RunsPanel } from './SidePanels'
import { type FlowData, type FlowNode, nodeTypes } from './WorkflowNode'
import { BRANCHING, CatalogContext, LabelsContext, TYPE_META, panel } from './shared'

type Tab = 'build' | 'step' | 'runs'

type Edge = FlowEdge
type Point = { x: number; y: number }

const EDGE_DEFAULTS = { type: 'flow' } as const
const marker = (color: string) => ({ type: MarkerType.ArrowClosed, width: 18, height: 18, color })

const GRID = 22
// A slightly bouncy spring: steps overshoot their new place a touch, then settle.
const SPRING_K = 190
const SPRING_D = 2 * Math.sqrt(SPRING_K) * 0.62

// Steps never sit on top of each other: this much clear space around every card.
const CLEARANCE = 24
type Box = { x: number; y: number; w: number; h: number }

function boxOf(n: FlowNode, at: Point = n.position): Box {
  return { x: at.x, y: at.y, w: n.measured?.width ?? 260, h: n.measured?.height ?? 130 }
}

function hits(a: Box, b: Box) {
  return (
    a.x < b.x + b.w + CLEARANCE &&
    b.x < a.x + a.w + CLEARANCE &&
    a.y < b.y + b.h + CLEARANCE &&
    b.y < a.y + a.h + CLEARANCE
  )
}

/** The nearest spot to `want` (on the grid) where this card touches nothing. */
function freeSpot(n: FlowNode, want: Point, others: Box[]): Point {
  for (let r = 0; r <= 20; r++) {
    const d = r * GRID
    const tries: Point[] =
      r === 0
        ? [want]
        : [
            { x: want.x, y: want.y + d },
            { x: want.x + d, y: want.y },
            { x: want.x - d, y: want.y },
            { x: want.x, y: want.y - d },
            { x: want.x + d, y: want.y + d },
            { x: want.x - d, y: want.y + d }
          ]
    for (const p of tries) {
      const b = boxOf(n, p)
      if (!others.some((o) => hits(b, o))) return p
    }
  }
  return want
}

function overlapping(nodes: FlowNode[]) {
  const boxes = nodes.map((n) => boxOf(n))
  return boxes.some((a, i) => boxes.some((b, j) => j > i && hits(a, b)))
}

function edgeId(e: { source: string; target: string; sourceHandle?: string | null }) {
  return `e-${e.source}-${e.target}-${e.sourceHandle ?? ''}`
}

/** What a line looks like for the selected run: did the run go this way, is it going now, or not at all. */
function edgeState(
  e: Edge,
  steps: Record<string, WorkflowRunStep>,
  runActive: boolean
): EdgeState {
  const src = steps[e.source]
  if (!src || (src.status !== 'done' && src.status !== 'failed')) return 'idle'
  const handle = e.sourceHandle ?? null
  const taken =
    src.branch === 'error'
      ? handle === 'error'
      : src.branch
        ? handle !== 'error' && (handle ?? 'yes') === src.branch
        : handle !== 'error' && src.status === 'done'
  if (!taken) return src.status === 'failed' && !runActive && handle !== 'error' ? 'idle' : 'skipped'
  const dst = steps[e.target]
  if (!dst) return runActive ? 'flowing' : 'idle'
  if (dst.status === 'running' || dst.status === 'resumed' || dst.status === 'waiting') return 'flowing'
  return dst.status === 'skipped' ? 'skipped' : 'done'
}

const DEFAULT_DATA: Record<Exclude<NodeType, 'trigger'>, NodeData> = {
  agent: { label: '', agent: '', instructions: '' },
  action: { label: '', agent: '', tool: '', args: {} },
  condition: { label: '', mode: 'rule', value: '{{previous}}', op: 'contains', compare: '' },
  approval: { label: '', title: '', message: '' },
  wait: { label: '', amount: 1, unit: 'hours' },
  notify: { label: '', message: '{{previous}}', email_owner: false },
  http: { label: '', method: 'GET', url: '', headers: {}, body: '', retries: 2 },
  set: { label: '', fields: { value: '{{previous}}' } },
  loop: { label: '', items: '{{previous}}', mode: 'map', instructions: '' },
  classify: {
    label: '',
    source: 'new_posts',
    items: '{{previous}}',
    target: '',
    not_target: '',
    keywords: '',
    examples: '',
    mode: 'smart',
    retries: 1
  }
}

function toFlow(graph: Graph): { nodes: FlowNode[]; edges: Edge[] } {
  return {
    nodes: graph.nodes.map((n) => ({
      id: n.id,
      type: n.type,
      position: n.position ?? { x: 0, y: 0 },
      data: { ...n.data },
      deletable: n.type !== 'trigger'
    })),
    edges: graph.edges.map((e) => ({
      id: e.id ?? edgeId(e),
      source: e.source,
      target: e.target,
      sourceHandle: e.sourceHandle ?? null,
      ...EDGE_DEFAULTS,
      data: { error: e.sourceHandle === 'error' }
    }))
  }
}

function toGraph(nodes: FlowNode[], edges: Edge[]): Graph {
  return {
    nodes: nodes.map((n) => {
      const { __status, __fresh, ...data } = n.data
      return { id: n.id, type: n.type as NodeType, position: n.position, data }
    }),
    edges: edges.map((e) => ({
      source: e.source,
      target: e.target,
      sourceHandle: (e.sourceHandle as GraphEdge['sourceHandle']) ?? null
    }))
  }
}

/** Moves steps to new places on a spring instead of jumping there. */
function useGlide(setNodes: Dispatch<SetStateAction<FlowNode[]>>) {
  const raf = useRef(0)
  const sim = useRef(new Map<string, { p: Point; v: Point; to: Point }>())
  useEffect(() => () => cancelAnimationFrame(raf.current), [])

  const glide = useCallback(
    (from: Record<string, Point>, to: Record<string, Point>) => {
      for (const [id, target] of Object.entries(to)) {
        const cur = sim.current.get(id)
        const start = cur?.p ?? from[id] ?? target
        sim.current.set(id, { p: { ...start }, v: cur?.v ?? { x: 0, y: 0 }, to: { ...target } })
      }
      cancelAnimationFrame(raf.current)
      let last = performance.now()
      const tick = (now: number) => {
        const dt = Math.min(0.034, (now - last) / 1000)
        last = now
        const frame = new Map<string, Point>()
        for (const [id, s] of sim.current) {
          for (const k of ['x', 'y'] as const) {
            s.v[k] += (SPRING_K * (s.to[k] - s.p[k]) - SPRING_D * s.v[k]) * dt
            s.p[k] += s.v[k] * dt
          }
          const still =
            Math.abs(s.to.x - s.p.x) < 0.3 &&
            Math.abs(s.to.y - s.p.y) < 0.3 &&
            Math.abs(s.v.x) + Math.abs(s.v.y) < 3
          if (still) {
            frame.set(id, s.to)
            sim.current.delete(id)
          } else frame.set(id, { x: s.p.x, y: s.p.y })
        }
        setNodes((ns) =>
          ns.map((n) => {
            const p = frame.get(n.id)
            return p && !n.dragging ? { ...n, position: p } : n
          })
        )
        if (sim.current.size) raf.current = requestAnimationFrame(tick)
      }
      raf.current = requestAnimationFrame(tick)
    },
    [setNodes]
  )

  const release = useCallback((ids: string[]) => {
    for (const id of ids) sim.current.delete(id)
  }, [])

  return { glide, release }
}

export function WorkflowCanvasPage() {
  const { workflowId } = useParams()
  return <WorkflowCanvas id={Number(workflowId)} />
}

/** The canvas: full page, or `embedded` as a panel inside the chat (with a close button). */
export function WorkflowCanvas({
  id,
  embedded = false,
  onClose,
  onDirtyChange
}: {
  id: number
  embedded?: boolean
  onClose?: () => void
  /** Unsaved edits on the canvas: the chat holds back changes from the team until they're saved. */
  onDirtyChange?: (dirty: boolean) => void
}) {
  return (
    <ReactFlowProvider key={id}>
      <Canvas id={id} embedded={embedded} onClose={onClose} onDirtyChange={onDirtyChange} />
    </ReactFlowProvider>
  )
}

function Canvas({
  id,
  embedded,
  onClose,
  onDirtyChange
}: {
  id: number
  embedded: boolean
  onClose?: () => void
  onDirtyChange?: (dirty: boolean) => void
}) {
  const navigate = useNavigate()
  const { resolvedTheme } = useTheme()
  const { fitView } = useReactFlow()
  const dark = resolvedTheme === 'dark'

  const catalog = useQuery({
    queryKey: ['workflow-catalog'],
    queryFn: fetchCatalog,
    staleTime: 5 * 60_000
  })
  const wf = useQuery({
    queryKey: ['workflow', id],
    queryFn: () => fetchWorkflow(id),
    enabled: Number.isFinite(id),
    refetchInterval: (q) =>
      q.state.data?.build_status === 'building'
        ? 900
        : q.state.data?.runs.some((r) => RUN_ACTIVE.includes(r.status))
          ? 3000
          : false
  })
  const data = wf.data
  const building = data?.build_status === 'building'

  const [nodes, setNodes, onNodesChange] = useNodesState<FlowNode>([])
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([])
  const { glide, release } = useGlide(setNodes)
  const nodesRef = useRef(nodes)
  nodesRef.current = nodes
  const [name, setName] = useState('')
  const [dirty, setDirty] = useState(false)
  const [tab, setTab] = useState<Tab>('build')
  const [selectedNode, setSelectedNode] = useState<string | null>(null)
  const [selectedRun, setSelectedRun] = useState<number | null>(null)
  const [fresh, setFresh] = useState<Set<string>>(new Set())
  const [saving, setSaving] = useState(false)
  const [starting, setStarting] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const loadedAt = useRef('')
  const known = useRef<Set<string>>(new Set())
  const wasBuilding = useRef(false)
  const dirtyRef = useRef(false)
  dirtyRef.current = dirty

  const run = useQuery({
    queryKey: ['workflow-run', selectedRun],
    queryFn: () => fetchRun(selectedRun as number),
    enabled: selectedRun !== null,
    refetchInterval: (q) =>
      q.state.data && RUN_ACTIVE.includes(q.state.data.status) ? 1500 : false
  })

  // Load the server's graph: always while the team builds (so it grows live), otherwise only
  // when there are no unsaved edits here.
  useEffect(() => {
    if (!data) return
    const finishedBuild = wasBuilding.current && !building
    wasBuilding.current = building
    if (data.updated_at === loadedAt.current && !finishedBuild) return
    if (dirtyRef.current && !building && !finishedBuild) return
    loadedAt.current = data.updated_at
    const firstLoad = known.current.size === 0 && !building
    const added = data.graph.nodes.map((n) => n.id).filter((n) => !known.current.has(n))
    for (const n of data.graph.nodes) known.current.add(n.id)
    if (!firstLoad && added.length) setFresh(new Set(added))
    const flow = toFlow(data.graph)
    const prev = new Map(nodesRef.current.map((n) => [n.id, n.position]))
    if (firstLoad || prev.size === 0) {
      setNodes(flow.nodes)
    } else {
      // Steps that moved glide to their new place; new steps spring out of the step they hang off.
      const from: Record<string, Point> = {}
      const to: Record<string, Point> = {}
      const starts = flow.nodes.map((n) => {
        const was = prev.get(n.id)
        const parentId = flow.edges.find((e) => e.target === n.id)?.source
        const start = was ?? (parentId ? prev.get(parentId) : undefined)
        if (!start || (start.x === n.position.x && start.y === n.position.y)) return n
        from[n.id] = start
        to[n.id] = n.position
        return { ...n, position: { ...start } }
      })
      setNodes(starts)
      glide(from, to)
    }
    setEdges(flow.edges)
    setName(data.name)
    if (finishedBuild) setDirty(false)
    if (finishedBuild && data.build_status === 'idle' && data.graph.nodes.length > 0) {
      const n = data.graph.nodes.filter((x) => x.type !== 'trigger').length
      toast.success(`"${data.name}" is ready`, {
        description: `${n} step${n === 1 ? '' : 's'}. Run it now, or switch it on.`
      })
    }
    if (building || firstLoad || finishedBuild)
      requestAnimationFrame(() => fitView({ padding: 0.3, duration: 450, maxZoom: 1 }))
  }, [data, building, setNodes, setEdges, fitView, glide])

  useEffect(() => {
    if (fresh.size === 0) return
    const t = window.setTimeout(() => setFresh(new Set()), 2600)
    return () => window.clearTimeout(t)
  }, [fresh])

  // Open on the runs tab when a workflow already has history and isn't being built.
  const opened = useRef(false)
  useEffect(() => {
    if (!data || opened.current) return
    opened.current = true
    if (data.build_status === 'idle' && data.runs.length > 0) setTab('runs')
  }, [data])

  // Questions from the team show on the Build tab.
  const asking = data?.build_status === 'asking'
  useEffect(() => {
    if (asking) setTab('build')
  }, [asking])

  useEffect(() => {
    onDirtyChange?.(dirty)
  }, [dirty, onDirtyChange])

  useEffect(() => {
    if (!dirty) return
    const warn = (e: BeforeUnloadEvent) => e.preventDefault()
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty])

  const stepByNode = useMemo(() => {
    const out: Record<string, WorkflowRunStep> = {}
    if (run.data && run.data.id === selectedRun)
      for (const s of run.data.steps ?? []) out[s.node_id] = s
    return out
  }, [run.data, selectedRun])
  const statusByNode = useMemo(() => {
    const out: Record<string, StepStatus> = {}
    for (const [nodeId, s] of Object.entries(stepByNode)) out[nodeId] = s.status
    return out
  }, [stepByNode])

  const runActive = !!run.data && RUN_ACTIVE.includes(run.data.status)

  // Say clearly when a run you're watching ends.
  const lastRunStatus = useRef<{ id: number; status: string } | null>(null)
  useEffect(() => {
    const r = run.data
    if (!r) return
    const prev = lastRunStatus.current
    lastRunStatus.current = { id: r.id, status: r.status }
    if (!prev || prev.id !== r.id || !RUN_ACTIVE.includes(prev.status as never) || RUN_ACTIVE.includes(r.status))
      return
    if (r.status === 'done') toast.success('Run finished', { description: r.summary?.slice(0, 140) || undefined })
    else if (r.status === 'failed') toast.error('Run failed', { description: r.error?.slice(0, 140) || undefined })
    else toast('Run stopped')
  }, [run.data])

  const labels = useMemo(
    () =>
      Object.fromEntries(
        nodes.map((n) => [n.id, (n.data.label as string) || TYPE_META[n.type as NodeType]?.label || n.id])
      ),
    [nodes]
  )

  // Who is working on which step right now: the builder while building, else the running step.
  const focusByNode = useMemo(() => {
    const out: Record<string, NonNullable<FlowData['__focus']>> = {}
    const f = data?.build_focus
    if (building && f?.node && f.text)
      out[f.node] = { agent: f.agent ?? 'chief_of_staff', text: f.text, mode: f.kind === 'thinking' ? 'think' : 'build' }
    if (!building && run.data && run.data.id === selectedRun) {
      for (const s of run.data.steps ?? []) {
        const node = nodes.find((n) => n.id === s.node_id)
        const agent = (node?.data.agent as string | undefined) || 'chief_of_staff'
        if (s.status === 'running' || s.status === 'resumed')
          out[s.node_id] = { agent, text: s.type === 'agent' ? 'Working on this…' : 'Doing this now', mode: 'run' }
        if (s.status === 'waiting')
          out[s.node_id] = {
            agent,
            text: s.error
              ? `Failed, trying again (${s.attempts} of ${Number(node?.data.retries) || s.attempts})`
              : s.card
                ? 'Waiting for your decision'
                : s.resume_at
                  ? 'Waiting for the timer'
                  : 'Waiting',
            mode: 'wait'
          }
      }
    }
    return out
  }, [data?.build_focus, building, run.data, selectedRun, nodes])

  const displayNodes = useMemo(
    () =>
      nodes.map((n) => ({
        ...n,
        draggable: !building,
        deletable: n.type !== 'trigger' && !building,
        data: {
          ...n.data,
          __status: statusByNode[n.id],
          __fresh: fresh.has(n.id),
          __focus: focusByNode[n.id]
        } as FlowData
      })),
    [nodes, building, statusByNode, fresh, focusByNode]
  )
  const focusEdge = building ? data?.build_focus?.edge : null
  const displayEdges = useMemo(
    () =>
      edges.map((e) => {
        const lit = !!focusEdge && e.source === focusEdge.source && e.target === focusEdge.target
        const state: EdgeState = building ? (lit ? 'lit' : 'idle') : edgeState(e, stepByNode, runActive)
        const error = e.sourceHandle === 'error'
        const color = edgeColor(state, error, dark)
        return {
          ...e,
          data: { state, error, color, fresh: fresh.has(e.target) },
          markerEnd: marker(color)
        }
      }),
    [edges, building, runActive, stepByNode, focusEdge, fresh, dark]
  )

  const handleNodesChange = useCallback(
    (changes: NodeChange<FlowNode>[]) => {
      const allowed = building
        ? changes.filter((c) => c.type === 'select' || c.type === 'dimensions')
        : changes
      onNodesChange(allowed)
      if (allowed.some((c) => c.type === 'position' || c.type === 'remove')) setDirty(true)
      if (allowed.some((c) => c.type === 'remove' && c.id === selectedNode)) setSelectedNode(null)
    },
    [building, onNodesChange, selectedNode]
  )

  const handleEdgesChange = useCallback(
    (changes: EdgeChange<Edge>[]) => {
      const allowed = building ? changes.filter((c) => c.type === 'select') : changes
      onEdgesChange(allowed)
      if (allowed.some((c) => c.type === 'remove')) setDirty(true)
    },
    [building, onEdgesChange]
  )

  const isValidConnection = useCallback(
    (c: Connection | Edge) => {
      if (building || c.source === c.target) return false
      const target = nodes.find((n) => n.id === c.target)
      return !!target && target.type !== 'trigger'
    },
    [building, nodes]
  )

  const onConnect = useCallback(
    (c: Connection) => {
      if (!isValidConnection(c)) return
      setEdges((eds) =>
        addEdge(
          {
            ...c,
            id: edgeId(c),
            ...EDGE_DEFAULTS,
            data: { error: c.sourceHandle === 'error', fresh: true }
          },
          eds
        )
      )
      setDirty(true)
    },
    [isValidConnection, setEdges]
  )

  const onNodeDragStart = useCallback(
    (_: unknown, __: FlowNode, dragged: FlowNode[]) => release(dragged.map((n) => n.id)),
    [release]
  )

  // Let go of a step and it settles onto the dot grid with a little bounce.
  const onNodeDragStop = useCallback(
    (_: unknown, __: FlowNode, dragged: FlowNode[]) => {
      const from: Record<string, Point> = {}
      const to: Record<string, Point> = {}
      const moving = new Set(dragged.map((n) => n.id))
      // Dropped on another step? It slides to the nearest free spot instead.
      const others = nodesRef.current.filter((n) => !moving.has(n.id)).map((n) => boxOf(n))
      for (const n of dragged) {
        from[n.id] = n.position
        const snapped = {
          x: Math.round(n.position.x / GRID) * GRID,
          y: Math.round(n.position.y / GRID) * GRID
        }
        to[n.id] = freeSpot(n, snapped, others)
        others.push(boxOf(n, to[n.id]))
      }
      glide(from, to)
    },
    [glide]
  )

  const onSelectionChange = useCallback(
    ({ nodes: picked }: { nodes: FlowNode[] }) => {
      const next = picked[0]?.id ?? null
      setSelectedNode(next)
      if (next) setTab('step')
    },
    []
  )

  function patchNode(nodeId: string, patch: Partial<NodeData>) {
    setNodes((ns) =>
      ns.map((n) => (n.id === nodeId ? { ...n, data: { ...n.data, ...patch } } : n))
    )
    setDirty(true)
  }

  function removeNode(nodeId: string) {
    setNodes((ns) => ns.filter((n) => n.id !== nodeId))
    setEdges((es) => es.filter((e) => e.source !== nodeId && e.target !== nodeId))
    setSelectedNode(null)
    setDirty(true)
  }

  function addStep(type: Exclude<NodeType, 'trigger'>) {
    const nums = nodes.map((n) => /^n(\d+)$/.exec(n.id)?.[1]).filter(Boolean).map(Number)
    const newId = `n${Math.max(0, ...nums) + 1}`
    const from =
      nodes.find((n) => n.id === selectedNode) ??
      [...nodes].sort((a, b) => b.position.y - a.position.y)[0]
    const position = from
      ? { x: from.position.x, y: from.position.y + 170 }
      : { x: 0, y: 0 }
    setNodes((ns) => [
      ...ns.map((n) => ({ ...n, selected: false })),
      {
        id: newId,
        type,
        position: from ? { ...from.position } : position,
        data: { ...DEFAULT_DATA[type] },
        selected: true,
        deletable: true
      }
    ])
    if (from) glide({ [newId]: from.position }, { [newId]: position })
    if (from)
      setEdges((es) =>
        addEdge(
          {
            id: `e-${from.id}-${newId}`,
            source: from.id,
            target: newId,
            sourceHandle: BRANCHING.includes(from.type as NodeType) ? 'yes' : null,
            targetHandle: null,
            ...EDGE_DEFAULTS,
            data: { fresh: true }
          },
          es
        )
      )
    setFresh(new Set([newId]))
    setSelectedNode(newId)
    setTab('step')
    setDirty(true)
  }

  function accept(next: WorkflowDetail) {
    setDirty(false)
    dirtyRef.current = false
    queryClient.setQueryData(['workflow', id], next)
    queryClient.invalidateQueries({ queryKey: ['workflows'] })
  }

  // Deleted while open in the chat: close the panel and go back to talking.
  const gone = embedded && wf.error instanceof ApiError && wf.error.status === 404
  useEffect(() => {
    if (!gone) return
    toast('That workflow was deleted.')
    onClose?.()
  }, [gone, onClose])

  // Opened with steps on top of each other (an older layout)? Lay it out again, once, by itself.
  // Never on a switched-on workflow: that one only changes when someone saves it.
  const autoTidied = useRef(false)
  useEffect(() => {
    if (autoTidied.current || !data || data.enabled || building || dirty || nodes.length < 2) return
    if (!nodes.every((n) => n.measured?.width)) return
    autoTidied.current = true
    if (overlapping(nodes)) tidy()
  }, [data, building, dirty, nodes])

  async function tidy() {
    try {
      accept(
        await saveWorkflow(id, {
          ...(dirty ? { name: name.trim() || 'New workflow', graph: toGraph(nodes, edges) } : {}),
          tidy: true
        })
      )
    } catch (err) {
      notifyError('Could not tidy it up', err)
    }
  }

  async function save(extra: { enabled?: boolean } = {}) {
    setSaving(true)
    try {
      accept(
        await saveWorkflow(id, {
          ...(dirty ? { name: name.trim() || 'New workflow', graph: toGraph(nodes, edges) } : {}),
          ...extra
        })
      )
      if (extra.enabled !== undefined)
        toast.success(extra.enabled ? 'Workflow switched on' : 'Workflow switched off')
      else toast.success('Saved')
    } catch (err) {
      notifyError(extra.enabled ? 'Not switched on yet' : 'Could not save', err)
    } finally {
      setSaving(false)
    }
  }

  async function refine(prompt: string) {
    try {
      const next = await buildWorkflow(prompt, id)
      queryClient.setQueryData(['workflow', id], next)
      setTab('build')
    } catch (err) {
      notifyError('Could not ask the team', err)
      throw err
    }
  }

  async function haltBuild() {
    try {
      queryClient.setQueryData(['workflow', id], await stopBuild(id))
      toast.success('Stopping: the team finishes the step it is on')
    } catch (err) {
      notifyError('Could not stop it', err)
    }
  }

  async function startRun() {
    setStarting(true)
    try {
      const started = await runWorkflow(id)
      setSelectedRun(started.id)
      setTab('runs')
      queryClient.setQueryData(['workflow-run', started.id], started)
      await queryClient.invalidateQueries({ queryKey: ['workflow', id] })
    } catch (err) {
      notifyError('Could not run it', err)
    } finally {
      setStarting(false)
    }
  }

  async function stopRun(runId: number) {
    try {
      queryClient.setQueryData(['workflow-run', runId], await cancelRun(runId))
      await queryClient.invalidateQueries({ queryKey: ['workflow', id] })
    } catch (err) {
      notifyError('Could not stop it', err)
    }
  }

  async function remove() {
    try {
      await deleteWorkflow(id)
      await queryClient.invalidateQueries({ queryKey: ['workflows'] })
      setDirty(false)
      if (embedded) onClose?.()
      else navigate('/apps/ai-team/workflows')
    } catch (err) {
      notifyError('Could not delete it', err)
    }
  }

  if (wf.error && !(embedded && wf.error instanceof ApiError && wf.error.status === 404))
    return <ErrorState error={wf.error} onRetry={() => wf.refetch()} />

  const selected = nodes.find((n) => n.id === selectedNode)
  const graphNodes: GraphNode[] = nodes.map((n) => ({ id: n.id, type: n.type as NodeType, data: n.data }))
  const earlier = graphNodes.filter((n) => n.type !== 'trigger' && n.id !== selectedNode)
  const errors = data?.errors ?? []
  const canRun = !!data && !building && !dirty && errors.length === 0
  const runHint = building
    ? 'Wait for the team to finish building.'
    : dirty
      ? 'Save your changes to run it.'
      : errors.length
        ? 'Fill in what the Build tab lists first.'
        : ''

  return (
    <CatalogContext.Provider value={catalog.data ?? null}>
      <LabelsContext.Provider value={labels}>
      <div
        className={cn(
          'flex flex-col gap-3',
          embedded
            ? 'h-full min-h-0'
            : '-m-6 h-[calc(100dvh-5.5rem)] min-h-[560px] bg-neutral-100 p-3 dark:bg-neutral-950'
        )}
      >
        {/* Header */}
        <div className={cn(panel, 'flex flex-wrap items-center gap-2 rounded-[36px] px-3 py-2.5')}>
          {embedded ? (
            <button type="button" className={iconBtn} onClick={onClose} aria-label="Close the canvas">
              <X className="size-4" />
            </button>
          ) : (
            <Link to="/apps/ai-team/workflows" className={iconBtn} aria-label="All workflows">
              <ArrowLeft className="size-4" />
            </Link>
          )}
          <input
            value={name}
            onChange={(e) => {
              setName(e.target.value)
              setDirty(true)
            }}
            disabled={!data || building}
            maxLength={120}
            aria-label="Workflow name"
            className="min-w-0 flex-1 rounded-full bg-transparent px-2 py-1 text-base font-bold text-neutral-900 outline-none hover:bg-neutral-50 focus:bg-neutral-50 disabled:hover:bg-transparent dark:text-white dark:hover:bg-neutral-900 dark:focus:bg-neutral-900"
          />
          {data && (
            <span
              className={cn(
                'inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold',
                building || data.build_status === 'asking'
                  ? 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400'
                  : data.enabled
                    ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400'
                    : 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300'
              )}
            >
              {building && <Loader2 className="size-3 animate-spin" />}
              {building
                ? 'Building'
                : data.build_status === 'asking'
                  ? 'Needs your answers'
                  : data.enabled
                    ? 'On'
                    : 'Off'}
            </span>
          )}
          <span className="hidden shrink-0 text-xs text-neutral-500 lg:inline dark:text-neutral-400">
            {data?.trigger}
          </span>

          <div className="ml-auto flex shrink-0 items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className={btnSecondary} disabled={!data || building}>
                  <Plus className="size-4" /> Add step
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-72 rounded-[36px] border border-neutral-200 bg-white p-2 shadow-2xl dark:border-neutral-800 dark:bg-neutral-900"
              >
                <DropdownMenuLabel className="px-3 text-xs text-neutral-500">
                  Adds below the selected step
                </DropdownMenuLabel>
                {NODE_TYPES.map((t) => {
                  const Icon = TYPE_META[t.type].icon
                  return (
                    <DropdownMenuItem
                      key={t.type}
                      onSelect={() => addStep(t.type)}
                      className="items-start gap-3 rounded-[24px] px-3 py-2"
                    >
                      <Icon className="mt-0.5 size-4" />
                      <span>
                        <span className="block text-sm font-semibold">{t.label}</span>
                        <span className="block text-xs text-neutral-500 dark:text-neutral-400">
                          {t.hint}
                        </span>
                      </span>
                    </DropdownMenuItem>
                  )
                })}
              </DropdownMenuContent>
            </DropdownMenu>

            <button
              type="button"
              className={btnSecondary}
              disabled={!dirty || saving || building}
              onClick={() => save()}
            >
              {saving ? (
                <Loader2 className="size-4 animate-spin" />
              ) : dirty ? (
                <Save className="size-4" />
              ) : (
                <Check className="size-4" />
              )}
              {dirty ? 'Save' : 'Saved'}
            </button>

            {data && (
              <button
                type="button"
                role="switch"
                aria-checked={data.enabled}
                disabled={building || saving || data.trigger_kind === 'manual'}
                title={
                  data.trigger_kind === 'manual'
                    ? 'Manual workflows run when you press Run now'
                    : data.enabled
                      ? 'Switch off'
                      : 'Switch on'
                }
                onClick={() => save({ enabled: !data.enabled })}
                className={cn(
                  'relative h-7 w-12 shrink-0 rounded-full transition-colors disabled:opacity-40',
                  data.enabled ? 'bg-emerald-500' : 'bg-neutral-300 dark:bg-neutral-700'
                )}
              >
                <span
                  className={cn(
                    'absolute top-1 size-5 rounded-full bg-white shadow transition-all',
                    data.enabled ? 'left-6' : 'left-1'
                  )}
                />
                <span className="sr-only">Switch on</span>
              </button>
            )}

            <button
              type="button"
              className={btnPrimary}
              disabled={!canRun || starting}
              title={runHint || 'Run it now'}
              onClick={startRun}
            >
              {starting ? <Loader2 className="size-4 animate-spin" /> : <Play className="size-4" />}
              <span className="hidden sm:inline">Run now</span>
            </button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className={iconBtn} aria-label="More">
                  <MoreHorizontal className="size-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-56 rounded-[36px] border border-neutral-200 bg-white p-2 shadow-2xl dark:border-neutral-800 dark:bg-neutral-900"
              >
                <DropdownMenuItem
                  onSelect={tidy}
                  disabled={!data || building}
                  className="gap-2 rounded-full px-3 py-2 font-semibold"
                >
                  <Sparkles className="size-4" /> Tidy up the layout
                </DropdownMenuItem>
                {embedded && (
                  <DropdownMenuItem
                    onSelect={() => navigate(`/apps/ai-team/workflows/${id}`)}
                    className="gap-2 rounded-full px-3 py-2 font-semibold"
                  >
                    <Maximize2 className="size-4" /> Open full page
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onSelect={() => setConfirmDelete(true)}
                  className="gap-2 rounded-full px-3 py-2 font-semibold text-red-600 focus:bg-red-50 focus:text-red-600 dark:text-red-400 dark:focus:bg-red-500/10"
                >
                  <Trash2 className="size-4" /> Delete workflow
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Canvas + side panel */}
        <div
          className={cn(
            'flex min-h-0 flex-1 flex-col gap-3',
            embedded ? 'overflow-y-auto 2xl:flex-row 2xl:overflow-visible' : 'lg:flex-row'
          )}
        >
          <div
            className={cn(
              panel,
              'relative min-h-[420px] flex-1 overflow-hidden',
              '[--wf-handle-ring:#ffffff] [--wf-handle:#0a0a0a] [--wf-ink:#171717] dark:[--wf-handle-ring:#171717] dark:[--wf-handle:#fafafa] dark:[--wf-ink:#fafafa]',
              // Handles light up as a dragged line snaps onto them.
              '[&_.react-flow__handle.connectingto]:shadow-[0_0_0_6px_rgba(16,185,129,0.3)] [&_.react-flow__handle.valid]:!bg-emerald-500'
            )}
          >
            {!data ? (
              <div className="absolute inset-0 animate-pulse bg-neutral-50 dark:bg-neutral-900" />
            ) : (
              <ReactFlow
                nodes={displayNodes}
                edges={displayEdges}
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                connectionLineComponent={ConnectionLine}
                connectionRadius={44}
                onNodeDragStart={onNodeDragStart}
                onNodeDragStop={onNodeDragStop}
                onNodesChange={handleNodesChange}
                onEdgesChange={handleEdgesChange}
                onConnect={onConnect}
                isValidConnection={isValidConnection}
                onSelectionChange={onSelectionChange}
                onPaneClick={() => setSelectedNode(null)}
                nodesConnectable={!building}
                elementsSelectable
                fitView
                fitViewOptions={{ padding: 0.3, maxZoom: 1 }}
                minZoom={0.25}
                maxZoom={1.6}
                deleteKeyCode={building ? null : ['Backspace', 'Delete']}
                colorMode={resolvedTheme === 'dark' ? 'dark' : 'light'}
                className="!bg-transparent"
              >
                <Background variant={BackgroundVariant.Dots} gap={22} size={1.4} />
                <Controls showInteractive={false} className="!rounded-[24px] !shadow-sm" />
              </ReactFlow>
            )}

            {data && nodes.length === 0 && (
              <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-4 text-center">
                {building ? (
                  <>
                    <ChiefOrb size="xl" state="thinking" />
                    <p className="text-lg font-bold text-neutral-900 dark:text-white">
                      Chief of Staff is reading your request
                    </p>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">
                      Steps appear here as the team adds them.
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">
                    {embedded
                      ? 'Nothing here yet. Use Add step, or tell the team in the chat.'
                      : 'Nothing here yet. Use Add step, or ask the team in the Build tab.'}
                  </p>
                )}
              </div>
            )}
          </div>

          <aside
            className={cn(
              panel,
              'flex min-h-[360px] w-full flex-col overflow-hidden',
              embedded ? '2xl:w-[340px]' : 'lg:w-[380px]'
            )}
          >
            <div className="p-3">
              <div className="grid grid-cols-3 gap-1 rounded-full bg-neutral-100 p-1 dark:bg-neutral-900">
                {(['build', 'step', 'runs'] as Tab[]).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTab(t)}
                    className={cn(
                      'rounded-full px-3 py-1.5 text-sm font-semibold transition-colors',
                      tab === t
                        ? 'bg-white text-neutral-900 shadow-sm dark:bg-neutral-800 dark:text-white'
                        : 'text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white'
                    )}
                  >
                    {t === 'build' ? 'Build' : t === 'step' ? 'Step' : 'Runs'}
                    {t === 'runs' && data && data.runs.length > 0 && (
                      <span className="ml-1 tabular-nums text-neutral-400">{data.runs.length}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="min-h-0 flex-1">
              {!data ? (
                <div className="space-y-3 p-4">
                  <div className="h-20 animate-pulse rounded-[24px] bg-neutral-100 dark:bg-neutral-900" />
                  <div className="h-40 animate-pulse rounded-[24px] bg-neutral-100 dark:bg-neutral-900" />
                </div>
              ) : tab === 'build' ? (
                <BuildPanel
                  wf={data}
                  dirty={dirty}
                  composer={!embedded}
                  onRefine={refine}
                  onStop={haltBuild}
                  onShowNode={(nodeId) =>
                    fitView({ nodes: [{ id: nodeId }], duration: 450, maxZoom: 1.1, padding: 0.6 })
                  }
                />
              ) : tab === 'step' ? (
                <div className="h-full overflow-y-auto px-4 pb-4">
                  {selected ? (
                    <NodeInspector
                      key={selected.id}
                      node={{ id: selected.id, type: selected.type as NodeType, data: selected.data }}
                      earlier={earlier}
                      hookUrl={data?.hook_path ? `${API_BASE_URL}${data.hook_path}` : ''}
                      memory={data?.memory?.[selected.id]}
                      readOnly={building}
                      onChange={(patch) => patchNode(selected.id, patch)}
                      onDelete={() => removeNode(selected.id)}
                    />
                  ) : (
                    <div className="rounded-[24px] bg-neutral-100 p-5 text-center dark:bg-neutral-900">
                      <p className="text-sm font-semibold text-neutral-900 dark:text-white">
                        Pick a step on the canvas
                      </p>
                      <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                        Click any step to edit it. Drag between the dots to connect steps; select a line
                        and press Delete to remove it.
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <RunsPanel
                  wf={data}
                  run={run.data}
                  selected={selectedRun}
                  canRun={canRun}
                  runHint={runHint}
                  starting={starting}
                  onRun={startRun}
                  onSelect={setSelectedRun}
                  onCancel={stopRun}
                />
              )}
            </div>
          </aside>
        </div>
      </div>

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Delete this workflow?"
        description="Runs in progress are stopped. This can't be undone."
        confirmLabel="Delete"
        destructive
        onConfirm={remove}
      />
      </LabelsContext.Provider>
    </CatalogContext.Provider>
  )
}
